import moment from 'moment';
import pick from 'lodash/pick';
import omitBy from 'lodash/omitBy';
import get from 'lodash/get';

import {
  CHANGE_REPEAT_FLOW_TYPES,
  RECURSION_OPTIONS,
  REPEATABLE_VALUES,
  WEEK_DAYS,
} from '../../components/ProviderCalendar/constants';
import { API_DATE_FORMAT, formatToAPIDateFormat } from './date-formatter';

export function repeatScheduleAdapter(option, schedule, formData) {
  const { scheduleSettingAttributes, ...restFormData } = formData;

  switch (option) {
    case RECURSION_OPTIONS.CURRENT_DAY: {
      return {
        ...restFormData,
        scheduleSettingAttributes: {
          ...scheduleSettingAttributes,
          endOn: formatToAPIDateFormat(
            get(scheduleSettingAttributes, 'startOn'),
          ),
        },
      };
    }
    case RECURSION_OPTIONS.CURRENT_AND_NEXT: {
      return formData;
    }
    case RECURSION_OPTIONS.ALL_DAYS: {
      return {
        ...restFormData,
        scheduleSettingAttributes: {
          ...scheduleSettingAttributes,
          startOn: formatToAPIDateFormat(
            get(schedule, 'scheduleSetting.startOn'),
          ),
        },
      };
    }
    default:
      return null;
  }
}

export function removeRepeatScheduleAdapter(option, schedule) {
  const { scheduleSetting, activeDate } = schedule;
  const { startTimeSlot, endTimeSlot, endOn, startOn } = scheduleSetting;
  const weeks = pick(scheduleSetting, WEEK_DAYS);
  const selectedDate = formatToAPIDateFormat(activeDate);

  switch (option) {
    case RECURSION_OPTIONS.CURRENT_DAY: {
      return {
        scheduleSettingAttributes: {
          ...weeks,
          startOn: selectedDate,
          endOn: selectedDate,
          startTime: startTimeSlot,
          endTime: endTimeSlot,
        },
      };
    }

    case RECURSION_OPTIONS.CURRENT_AND_NEXT: {
      return {
        scheduleSettingAttributes: {
          ...weeks,
          startOn: selectedDate,
          startTime: startTimeSlot,
          endTime: endTimeSlot,
          endOn: endOn ? formatToAPIDateFormat(endOn) : endOn,
        },
      };
    }

    case RECURSION_OPTIONS.ALL_DAYS: {
      return {
        scheduleSettingAttributes: {
          ...weeks,
          startTime: startTimeSlot,
          endTime: endTimeSlot,
          startOn: formatToAPIDateFormat(startOn),
          endOn: endOn ? formatToAPIDateFormat(endOn) : endOn,
        },
      };
    }

    default:
      return null;
  }
}

export function noRepeatScheduleAdapter(schedule) {
  const { scheduleSetting, activeDate } = schedule;
  const { startTimeSlot, endTimeSlot, endOn } = scheduleSetting;
  const weeks = pick(scheduleSetting, WEEK_DAYS);

  const startOn = moment
    .utc(activeDate)
    .add(1, 'd')
    .format(API_DATE_FORMAT);

  return {
    scheduleSettingAttributes: {
      ...weeks,
      startTime: startTimeSlot,
      endTime: endTimeSlot,
      startOn,
      endOn: endOn ? formatToAPIDateFormat(endOn) : endOn,
    },
  };
}

export function getPrevDayRepeatableAdapter(schedule) {
  const { scheduleSetting, activeDate } = schedule;
  const { startTimeSlot, endTimeSlot, startOn } = scheduleSetting;
  const weeks = pick(scheduleSetting, WEEK_DAYS);

  const endOn = moment
    .utc(activeDate)
    .subtract(1, 'd')
    .format(API_DATE_FORMAT);

  return {
    scheduleSettingAttributes: {
      ...weeks,
      startTime: startTimeSlot,
      endTime: endTimeSlot,
      startOn: formatToAPIDateFormat(startOn),
      endOn,
    },
  };
}

export function getRepeatFlow(repeatValue) {
  switch (repeatValue) {
    case REPEATABLE_VALUES.NO_REPEAT:
      return CHANGE_REPEAT_FLOW_TYPES.NO_REPEAT;

    case REPEATABLE_VALUES.CUSTOM:
      return CHANGE_REPEAT_FLOW_TYPES.UPDATE_CUSTOM_REPEAT;

    default:
      return CHANGE_REPEAT_FLOW_TYPES.DEFAULT;
  }
}

export function scheduleFormAdapter({
  patientType,
  colorMarkerHex,
  location,
  service,
} = {}) {
  return {
    patientType,
    colorMarkerHex,
    locationId: get(location, 'id'),
    serviceId: get(service, 'id'),
    scheduleSettingAttributes: {},
  };
}

export function scheduleCurrentDaySettingsAttrsAdapter({
  scheduleSetting,
  activeDate,
}) {
  const { startTimeSlot, endTimeSlot } = scheduleSetting;
  const selectedDate = formatToAPIDateFormat(activeDate);

  return {
    startOn: selectedDate,
    startTime: startTimeSlot,
    endTime: endTimeSlot,
  };
}

export function scheduleOriginDaySettingsAttrsAdapter({ scheduleSetting }) {
  const { startTimeSlot, endTimeSlot, startOn, endOn } = scheduleSetting;

  return {
    startOn: formatToAPIDateFormat(startOn),
    endOn,
    startTime: startTimeSlot,
    endTime: endTimeSlot,
  };
}

export function isLastDayOfRepeat({
  scheduleSetting: { endOn } = {},
  activeDate,
} = {}) {
  return moment.utc(endOn).isSame(activeDate, 'date');
}

export function isOnlyEndRepeatDateChange(selectedSchedule, repeat) {
  const {
    isRepeatable,
    isStrictWeeklyRepeat,
    scheduleSetting,
  } = selectedSchedule;
  const { repeatValue, repeatData } = repeat;

  const scheduleWeeks = pick(scheduleSetting, WEEK_DAYS);
  const activeWeeks = Object.keys(omitBy(scheduleWeeks, value => !value));
  const repeateWeeks = Object.keys(pick(repeatData, WEEK_DAYS));

  if (
    isRepeatable &&
    !isStrictWeeklyRepeat &&
    repeatValue === REPEATABLE_VALUES.CUSTOM
  ) {
    return activeWeeks.join('_') === repeateWeeks.join('_');
  }

  return false;
}

export function isFirstDayOfCustomRepeatChange(
  selectedSchedule,
  recursionFlow,
) {
  const { activeDate, scheduleSetting } = selectedSchedule;
  const { startOn } = scheduleSetting;
  const isFirstDate = moment.utc(activeDate).isSame(startOn, 'date');
  const isCustomChange =
    recursionFlow === CHANGE_REPEAT_FLOW_TYPES.UPDATE_CUSTOM_REPEAT;

  return isCustomChange && isFirstDate;
}



// WEBPACK FOOTER //
// ./src/utils/schedule/repeat-schedule-adapter.js